import { css, html, LitElement } from 'lit';

import { RequesterMixin } from '@brightspace-ui/core/mixins/provider-mixin.js';
import { SkeletonMixin } from '@brightspace-ui/core/components/skeleton/skeleton-mixin.js';

import { getTenantBadges } from '../helpers/helpers.js';

class PMEnvironment extends SkeletonMixin(RequesterMixin(LitElement)) {

  static get properties() {
    return {
      environment: { type: Object },
      _environment: { type: Object },
      _providers: { type: Array },
      _admins: { type: Array },
      _employers: { type: Array },
      _expandedGroups: { type: Object },
      environmentId: { type: String, attribute: 'environment-id' },
      showHeader: { type: Boolean, attribute: 'show-header' },
      isDemoEnv: { type: Boolean, attribute: 'is-demo-env' },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          display: block;
          --shell-text: #1f2937;
          --shell-muted: #6b7280;
          --shell-muted2: #9ca3af;
          --shell-border: #e5e7eb;
          --shell-accent: #7c3aed;
          --shell-accent2: #6d28d9;
          --shell-good: #16a34a;
          --shell-provider: #10b981;
          --shell-employer: #f59e0b;
          --shell-admin: #8b5cf6;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
          color: var(--shell-text);
        }

        /* Tenant Groups */
        .tenant-groups {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .tenant-group {
          border: 1px solid var(--shell-border);
          border-radius: 12px;
          background: #ffffff;
          overflow: hidden;
        }

        .group-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          cursor: pointer;
          user-select: none;
          transition: background 0.15s ease;
          border-left: 3px solid transparent;
        }

        .group-header:hover {
          background: #f9fafb;
        }

        .tenant-group.providers .group-header {
          border-left-color: var(--shell-provider);
        }

        .tenant-group.employers .group-header {
          border-left-color: var(--shell-employer);
        }

        .tenant-group.admins .group-header {
          border-left-color: var(--shell-admin);
        }

        .group-header-left {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .expand-icon {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--shell-muted);
          transition: transform 0.2s ease;
        }

        .tenant-group.expanded .expand-icon {
          transform: rotate(90deg);
        }

        .group-title {
          font-size: 14px;
          font-weight: 650;
          color: var(--shell-text);
          margin: 0;
        }

        .group-count {
          font-size: 12px;
          font-weight: 600;
          color: var(--shell-muted);
          background: #f3f4f6;
          padding: 2px 8px;
          border-radius: 999px;
          border: 1px solid var(--shell-border);
        }

        .group-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.25s ease-out;
        }

        .tenant-group.expanded .group-content {
          max-height: 2000px;
          transition: max-height 0.4s ease-in;
          overflow-y: scroll;
        }

        .group-content-inner {
          padding: 4px 12px 12px 12px;
          border-top: 1px solid var(--shell-border);
        }

        /* Empty state */
        .empty-state {
          padding: 24px 16px;
          text-align: center;
          color: var(--shell-muted);
          font-size: 13px;
        }

        /* Tenant Row */
        a.tenant-row {
          display: flex;
          align-items: center;
          padding: 10px 12px;
          margin: 6px 0;
          border-radius: 10px;
          border: 1px solid var(--shell-border);
          background: #ffffff;
          gap: 12px;
          text-decoration: none;
          color: inherit;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
        }

        a.tenant-row:first-child {
          margin-top: 0;
        }

        a.tenant-row:last-child {
          margin-bottom: 0;
        }

        .tenant-row profile-image {
          min-width: auto;
          flex-shrink: 0;
        }

        .tenant-row:hover {
          background: #fafafa;
          border-color: #d1d5db;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
        }

        .tenant-info {
          flex: 1;
          min-width: 0;
        }

        .tenant-name {
          color: var(--shell-text);
          font-weight: 600;
          font-size: 14px;
          line-height: 1.3;
        }

        .tenant-guid-row {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 2px;
        }

        .tenant-guid {
          color: var(--shell-muted);
          font-size: 11px;
          font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
        }

        .copy-btn {
          appearance: none;
          border: none;
          background: transparent;
          padding: 2px;
          cursor: pointer;
          color: var(--shell-muted2);
          opacity: 0;
          transition: opacity 0.15s ease, color 0.15s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .copy-btn:hover {
          color: var(--shell-accent);
        }

        .tenant-row:hover .copy-btn {
          opacity: 1;
        }

        .copy-btn svg {
          width: 14px;
          height: 14px;
        }

        /* Badges */
        .tenant-badges {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        .badge {
          display: inline-flex;
          align-items: center;
          padding: 3px 8px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          background: #f3f4f6;
          color: var(--shell-muted);
          border: 1px solid var(--shell-border);
          white-space: nowrap;
        }

        .badge.label {
          background: rgba(6, 182, 212, 0.1);
          color: #0891b2;
          border-color: rgba(6, 182, 212, 0.25);
        }

        .badge.type-provider {
          background: rgba(16, 185, 129, 0.1);
          color: #059669;
          border-color: rgba(16, 185, 129, 0.25);
        }

        .badge.type-employer {
          background: rgba(245, 158, 11, 0.1);
          color: #b45309;
          border-color: rgba(245, 158, 11, 0.25);
        }

        .badge.type-admin {
          background: rgba(139, 92, 246, 0.1);
          color: #7c3aed;
          border-color: rgba(139, 92, 246, 0.25);
        }

        .badge.state-success {
          background: rgba(22, 163, 74, 0.1);
          color: #15803d;
          border-color: rgba(22, 163, 74, 0.25);
        }

        .badge.state-alert {
          background: rgba(245, 158, 11, 0.1);
          color: #b45309;
          border-color: rgba(245, 158, 11, 0.25);
        }

        .badge.state-none {
          background: #f3f4f6;
          color: var(--shell-muted);
          border-color: var(--shell-border);
        }

        /* Loading skeleton */
        .skeleton-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          margin: 6px 0;
        }

        .skeleton-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }

        .skeleton-text {
          flex: 1;
        }

        .skeleton-line {
          height: 12px;
          border-radius: 4px;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }

        .skeleton-line.short {
          width: 60%;
          margin-top: 6px;
          height: 10px;
        }

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `];
  }

  constructor() {
    super();
    this._environment = { tenants: [] };
    this._expandedGroups = { providers: false, employers: false, admins: false };
    this.skeleton = true;
  }

  connectedCallback() {
    super.connectedCallback();
    this.client = this.requestInstance('d2l-pm-client');
  }

  _processTenants(environment) {
    if (environment?.status?.status === 'error') {
      this._environmentError = true;
      environment.tenants = [];
    }
    this._providers = (environment.tenants || []).filter(env => env.type === 'provider');
    this._providers.sort((a, b) => a.name.localeCompare(b.name));

    this._employers = (environment.tenants || []).filter(env => env.type === 'employer');
    this._employers.sort((a, b) => a.name.localeCompare(b.name));

    this._admins = (environment.tenants || []).filter(env => env.type === 'admin');
    this._admins.sort((a, b) => a.name.localeCompare(b.name));

    environment.tenants = [...this._providers, ...this._employers, ...this._admins];
    environment.id = environment.id ?? this.environmentId;

    this._environment = environment;
    this.skeleton = false;
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('environment') && this.environment) {
      this._processTenants(this.environment);
    }
  }

  async updated() {
    if ((!this.environmentId && !this._environment.id) || !this.skeleton) return;
    const environment = await this.client.fetchEnvironment(this.environmentId);
    this._processTenants(environment);
  }

  _toggleGroup(groupName) {
    return () => {
      this._expandedGroups = {
        ...this._expandedGroups,
        [groupName]: !this._expandedGroups[groupName],
      };
    };
  }

  _copyGuid(tenant) {
    return async e => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      e?.stopImmediatePropagation?.();
      const guid = tenant.id;
      await navigator.clipboard.writeText(guid);
      this.client.createAlert('success', `Copied ${guid} to clipboard`);
    };
  }

  _renderExpandIcon() {
    return html`
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    `;
  }

  _renderCopyIcon() {
    return html`
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
      </svg>
    `;
  }

  _renderTenantGroup(groupName, title, list, typeClass) {
    const isExpanded = this._expandedGroups[groupName];
    const count = list?.length || 0;

    return html`
      <div class="tenant-group ${typeClass} ${isExpanded ? 'expanded' : ''}">
        <div class="group-header" @click="${this._toggleGroup(groupName)}" role="button" tabindex="0" aria-expanded="${isExpanded}">
          <div class="group-header-left">
            <span class="expand-icon">${this._renderExpandIcon()}</span>
            <h4 class="group-title">${title}</h4>
          </div>
          <span class="group-count">${count}</span>
        </div>
        <div class="group-content">
          <div class="group-content-inner">
            ${this.skeleton ? this._renderSkeletonRows(3) : this._renderTenantList(list, typeClass)}
          </div>
        </div>
      </div>
    `;
  }

  _renderSkeletonRows(count) {
    return html`
      ${Array(count).fill(0).map(() => html`
        <div class="skeleton-row">
          <div class="skeleton-avatar"></div>
          <div class="skeleton-text">
            <div class="skeleton-line"></div>
            <div class="skeleton-line short"></div>
          </div>
        </div>
      `)}
    `;
  }

  _renderTenantList(list, typeClass) {
    if (!list || list.length === 0) {
      return html`<div class="empty-state">No tenants in this category</div>`;
    }

    return html`
      ${list.map(tenant => {
      let url = `/tenants/${this._environment.id}/${tenant.id}`;
      if (this._environment.fussUrl) url = `${url}?fuss=yes`;
      const badges = getTenantBadges(tenant);
      const typeBadgeClass = typeClass.replace('s', ''); // providers -> provider

      return html`
          <a href="${url}" class="tenant-row">
            <profile-image .user="${tenant}" text-size="hidden"></profile-image>
            <div class="tenant-info">
              <div class="tenant-name">${tenant.name}</div>
              ${this.isDemoEnv ? '' : html`
              <div class="tenant-guid-row">
                <span class="tenant-guid">${tenant.id}</span>
                <button class="copy-btn" @click="${this._copyGuid(tenant)}" title="Copy GUID">
                  ${this._renderCopyIcon()}
                </button>
              </div>
              `}
            </div>
            <div class="tenant-badges">
              ${this.isDemoEnv && tenant.tenantLabels?.length ? tenant.tenantLabels.map(label => html`
                <span class="badge label">${label}</span>
              `) : ''}
              ${badges.map(badge => html`
                <span class="badge state-${badge.state}" title="${badge.tooltip}">${badge.text}</span>
              `)}
              <span class="badge type-${typeBadgeClass}">${typeBadgeClass.charAt(0).toUpperCase() + typeBadgeClass.slice(1)}</span>
            </div>
          </a>
        `;
    })}
    `;
  }

  render() {
    return html`
      <div class="tenant-groups">
        ${this._renderTenantGroup('providers', 'Providers', this._providers, 'providers')}
        ${this._renderTenantGroup('employers', 'Employers', this._employers, 'employers')}
        ${this._renderTenantGroup('admins', 'Admins', this._admins, 'admins')}
      </div>
    `;
  }
}

window.customElements.define('pm-environment', PMEnvironment);
